import { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useRevenueCat, PRO_ENTITLEMENT } from '@/hooks/useRevenueCat';
import { THERAPIST_BILLING_ENABLED } from '@/lib/therapistBilling';


export type SubscriptionSource = 'stripe' | 'revenuecat' | 'none';

interface TherapistSubscriptionStatus {
  isSubscribed: boolean;
  source: SubscriptionSource;
  expiresAt: string | null;
  loading: boolean;
  error: string | null;
}

/**
 * Unified hook for checking therapist subscription status
 * - Uses RevenueCat for iOS native app
 * - Uses Stripe (via check-subscription) for web
 */
// Test/comp accounts that bypass the therapist paywall entirely
const THERAPIST_PAYWALL_BYPASS_EMAILS = new Set<string>([
  'david.anilus11@gmail.com',
]);

export function useTherapistSubscription() {
  const { user } = useAuth();
  const isNativePlatform = Capacitor.isNativePlatform();
  const isBypassed = !!user?.email && THERAPIST_PAYWALL_BYPASS_EMAILS.has(user.email.toLowerCase());

  
  // RevenueCat state (for iOS)
  const {
    isInitialized: rcInitialized,
    isLoading: rcLoading,
    error: rcError,
    customerInfo,
    hasProAccess,
    presentPaywall,
    restorePurchases,
  } = useRevenueCat();

  // Stripe state (for web)
  const [stripeStatus, setStripeStatus] = useState<{
    subscribed: boolean;
    expiresAt: string | null;
  } | null>(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);

  // Check Stripe subscription (web only)
  const checkStripeSubscription = useCallback(async () => {
    if (!THERAPIST_BILLING_ENABLED) return;
    if (!user || isNativePlatform) return;


    setStripeLoading(true);
    setStripeError(null);

    try {
      const { data, error } = await supabase.functions.invoke('check-therapist-subscription');
      
      if (error) throw error;
      
      setStripeStatus({
        subscribed: data?.subscribed ?? false,
        expiresAt: data?.subscription_end ?? null,
      });
    } catch (err) {
      console.error('Error checking Stripe subscription:', err);
      setStripeError(err instanceof Error ? err.message : 'Failed to check subscription');
      setStripeStatus({ subscribed: false, expiresAt: null });
    } finally {
      setStripeLoading(false);
    }
  }, [user, isNativePlatform]);

  // Auto-check Stripe subscription on mount and user change
  useEffect(() => {
    if (!isNativePlatform && user) {
      checkStripeSubscription();
    }
  }, [user, isNativePlatform, checkStripeSubscription]);

  // Compute unified status
  const getStatus = useCallback((): TherapistSubscriptionStatus & { isPaywallReady: boolean } => {
    // Billing disabled: everyone has access, no paywall
    if (!THERAPIST_BILLING_ENABLED) {
      return {
        isSubscribed: true,
        source: 'none',
        expiresAt: null,
        loading: false,
        error: null,
        isPaywallReady: true,
      };
    }

    // Bypass paywall for designated test accounts
    if (isBypassed) {

      return {
        isSubscribed: true,
        source: 'stripe',
        expiresAt: null,
        loading: false,
        error: null,
        isPaywallReady: true,
      };
    }


    // Native platform: use RevenueCat
    if (isNativePlatform) {
      // Only show loading if RevenueCat is still initializing AND hasn't errored
      const loading = (rcLoading || !rcInitialized) && !rcError;
      
      // Paywall is ready once SDK is initialized (don't wait for customer info)
      const isPaywallReady = rcInitialized;
      
      // Get expiration from RevenueCat entitlement if available
      let expiresAt: string | null = null;
      if (customerInfo?.entitlements?.active?.[PRO_ENTITLEMENT]) {
        const entitlement = customerInfo.entitlements.active[PRO_ENTITLEMENT];
        expiresAt = entitlement.expirationDate ?? null;
      }

      return {
        isSubscribed: hasProAccess,
        source: hasProAccess ? 'revenuecat' : 'none',
        expiresAt,
        loading,
        error: rcError,
        isPaywallReady,
      };
    }

    // Web: use Stripe - paywall always ready (no SDK init needed)
    return {
      isSubscribed: stripeStatus?.subscribed ?? false,
      source: stripeStatus?.subscribed ? 'stripe' : 'none',
      expiresAt: stripeStatus?.expiresAt ?? null,
      loading: stripeLoading,
      error: stripeError,
      isPaywallReady: true,
    };
  }, [
    isBypassed,
    isNativePlatform,
    rcLoading,
    rcInitialized,
    rcError,
    customerInfo,
    hasProAccess,
    stripeStatus,
    stripeLoading,
    stripeError,
  ]);


  // Subscribe action - presents paywall on iOS, creates Stripe checkout on web
  const subscribe = useCallback(async (): Promise<boolean> => {
    // Billing disabled: nothing to purchase, treat as success
    if (!THERAPIST_BILLING_ENABLED) return true;

    if (isNativePlatform) {

      // iOS: Present RevenueCat paywall
      return await presentPaywall();
    } else {
      // Web: Create Stripe checkout
      try {
        const { data, error } = await supabase.functions.invoke('create-therapist-checkout');
        
        if (error) throw error;
        
        if (data?.url) {
          window.location.href = data.url;
          return true;
        }
        return false;
      } catch (err) {
        console.error('Error creating checkout:', err);
        return false;
      }
    }
  }, [isNativePlatform, presentPaywall]);

  // Refresh subscription status
  const refresh = useCallback(async () => {
    if (isNativePlatform) {
      // RevenueCat auto-refreshes via the hook
      return;
    }
    await checkStripeSubscription();
  }, [isNativePlatform, checkStripeSubscription]);

  const status = getStatus();

  return {
    ...status,
    isNativePlatform,
    subscribe,
    refresh,
    restorePurchases: isNativePlatform ? restorePurchases : undefined,
  };
}
